import clsx from 'clsx';
import { DocumentIcon } from '@/components/icons/DocumentIcon';
import PoolStatusPill from '@/components/pools/PoolStatusPill';
import { useAuthStore } from '@/stores/authStore';
import { PoolNotification } from '@/types/notification';
import { getNotificationConfig, DOCUMENT_ICON_COLORS } from '@/utils/notificationUtils';
import { isInvestor } from '@/utils/user';

interface NotificationCardProps {
  notification: PoolNotification;
  variant?: 'compact' | 'full';
  onClick?: (poolId: number) => void;
}

export default function NotificationCard({ notification, variant = 'full', onClick }: NotificationCardProps) {
  const { user } = useAuthStore();
  const userIsInvestor = isInvestor(user);
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const poolInfo = notification.pool_id ? `Pool: #${notification.pool_id}` : '';
  const isCompact = variant === 'compact';
  const containerClass = isCompact ? 'p-3 gap-2' : 'p-4 sm:p-5 gap-3';
  const contentGapClass = isCompact ? 'gap-1.5' : 'gap-2';
  const titleSizeClass = isCompact ? 'text-sm' : 'text-sm sm:text-base';
  const bodySizeClass = isCompact ? 'text-xs' : 'text-xs sm:text-sm';
  const dateSizeClass = isCompact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base';
  const poolInfoSizeClass = 'text-xs';
  const config = getNotificationConfig(notification.name);
  const poolStatus = config?.status;
  const showStatusPill = !!poolStatus;
  const showDocumentIcon = !!config?.showDocumentIcon;
  const documentIconColor = config?.color && DOCUMENT_ICON_COLORS[config.color];

  const handleClick = () => {
    if (notification.pool_id) {
      onClick?.(notification.pool_id);
    }
  };

  return (
    <div
      className={clsx(
        'w-full bg-white overflow-hidden rounded-xl border border-[#E5E7EB] flex justify-start items-start min-h-[100px]',
        containerClass,
        {
          'cursor-pointer': !!onClick,
        }
      )}
      onClick={handleClick}
    >
      <div className={clsx('flex-1 flex flex-col justify-start items-start', contentGapClass)}>
        {/* Status Pill or Document Icon */}
        {showDocumentIcon ? (
          <DocumentIcon className={documentIconColor!} />
        ) : (
          showStatusPill && <PoolStatusPill status={poolStatus} isInvestor={userIsInvestor} />
        )}

        {/* Title and Body */}
        <div className={clsx('w-full flex flex-col justify-start items-start', contentGapClass)}>
          <div className={clsx('text-black', titleSizeClass, 'font-medium break-words w-full')}>
            {notification.title}
          </div>
          {notification.description && !isCompact && (
            <div className="w-full">
              <span className={clsx('text-[#767676]', bodySizeClass, 'font-medium break-words')}>
                {notification.description}
              </span>
            </div>
          )}
        </div>

        {/* Pool Info */}
        {poolInfo && (
          <div
            className={clsx('text-[#B2B2B2]', poolInfoSizeClass, 'font-normal underline leading-relaxed break-words')}
          >
            {poolInfo}
          </div>
        )}
      </div>

      {/* Date and unread indicator */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className={clsx('text-black', dateSizeClass, 'font-medium break-words whitespace-nowrap')}>
          {formatDate(notification.created_at)}
        </div>
        {!notification.is_read && <div className="w-2.5 h-2.5 rounded-full bg-[#DC2626] flex-shrink-0" />}
      </div>
    </div>
  );
}
